import { useMutation, useQueryClient } from "@tanstack/react-query";
import { dispatch } from "store";
import { openSnackbar } from "store/slices/snackbar";

interface MutationOptions<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  onSuccess?: () => void;
  queryKey?: string[];
}

export function useMutationHook<
  TData = unknown,
  TError = unknown,
  TVariables = unknown
>({ mutationFn, onSuccess, queryKey }: MutationOptions<TData, TVariables>) {
  const queryClient = useQueryClient();

  const mutation = useMutation<TData, TError, TVariables>(mutationFn, {
    onSuccess: () => {
      if (onSuccess) onSuccess();
      if (queryKey) queryClient.invalidateQueries(queryKey);
    },
    onError: (error: TError) => {
      // Determine message based on queryKey
      const errorMessage =
        queryKey && queryKey.includes("subscriptionCancellation")
          ? "Failed to cancel subscription"
          : "Failed to fetch data";

      // notify user
      dispatch(
        openSnackbar({
          open: true,
          message: errorMessage,
          alert: { color: "error" },
        })
      );
    },
  });

  return mutation;
}
